import { FC, useCallback } from 'react';
import {
  Control,
  Controller,
  useWatch,
  ControllerRenderProps,
  UseFormReset,
  UseFormGetValues,
} from 'react-hook-form';
import { AddonFormData, AddonType, AddonTypeData, TOption } from '@src/types';
import {
  InputSelect,
  Input,
  RadioGroupItem,
  RadioGroup,
  Switch,
} from '@features/ui';
import {
  addonTypeData,
  drawdownData,
  drawdownTypeData,
  extraAllowedPayoutTimeData,
  performanceAccountFeeData,
} from '@features/admin/productsAddons/productsAddonsData.ts';
import { AddonEvalDiscount } from '@features/admin/productsAddons';

interface ChooseAccountTypeProps {
  control: Control<AddonTypeData>;
  reset: UseFormReset<AddonTypeData>;
  getValues: UseFormGetValues<AddonTypeData>;
  isEdit?: boolean;
  productOptions: TOption[];
}
export const ChooseAccountType: FC<ChooseAccountTypeProps> = ({
  control,
  isEdit,
  getValues,
  reset,
  productOptions,
}) => {
  const addonType = useWatch({ control, name: 'addon_type' });

  const renderAddonContent = useCallback(() => {
    switch (addonType) {
      case AddonType.PaPayoutEveryXDays:
      case AddonType.EvalQualifyInXTradingDays:
        return (
          <Controller
            control={control}
            name="addon_trading_days_required"
            render={({ field }) => (
              <div className={'flex flex-col gap-3.5 flex-1 mt-6'}>
                <p>No. of trading days required</p>
                <Input
                  className={'w-[80px]'}
                  placeholder={'0'}
                  textSize={'xs'}
                  type={'number'}
                  variant={'secondary'}
                  {...field}
                />
              </div>
            )}
          />
        );
      case AddonType.EvalReducedPaLifetimeFee:
        return (
          <Controller
            control={control}
            name="addon_lifetime_performance_account_package_id"
            render={({ field }) => (
              <div className={'flex flex-col gap-3.5 flex-1 mt-6'}>
                <p>PA Package</p>
                <InputSelect
                  className={'text-xs'}
                  options={productOptions.map((item) => ({
                    value: item.value.toString(),
                    label: item.label,
                  }))}
                  placeholder={'Please Select'}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                />
              </div>
            )}
          />
        );
      case AddonType.EvalDiscountXPercentReset:
        return (
          <AddonEvalDiscount
            control={control as unknown as Control<AddonFormData>}
          />
        );
      case AddonType.PaAllowExtraAmountOnPayout:
        return (
          <>
            <Controller
              control={control}
              name="addon_extra_amount_allowed_payout"
              render={({ field }) => (
                <div className={'flex flex-col gap-3.5 flex-1 mt-6'}>
                  <p>Amount (USD) $</p>
                  <Input
                    className={'w-[150px]'}
                    placeholder={'0'}
                    textSize={'xs'}
                    type={'number'}
                    variant={'secondary'}
                    {...field}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="addon_extra_allowed_payout_time"
              render={({ field }) => (
                <div className={'flex flex-col gap-3.5 flex-1 mt-6'}>
                  <p>Addon extra amount allowed</p>
                  <InputSelect
                    className={'text-xs'}
                    options={extraAllowedPayoutTimeData}
                    placeholder={'Please Select'}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                </div>
              )}
            />
          </>
        );
      case AddonType.PaChange30PercentRuleToXPercent:
        return (
          <div className={'flex lg:flex-row flex-col lg:items-end gap-7'}>
            <Controller
              control={control}
              name="addon_new_percent_override_default"
              render={({ field }) => (
                <div className={'flex flex-col gap-3.5 mt-6'}>
                  <p>New Percent (%)</p>
                  <Input
                    className={'w-[80px]'}
                    placeholder={'0'}
                    textSize={'xs'}
                    type={'number'}
                    variant={'secondary'}
                    {...field}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name={`addon_disable_percent_default`}
              render={({ field }) => (
                <div className={'mb-2.5'}>
                  <Switch
                    checked={field.value}
                    label={'Addon disable percent default'}
                    onCheckedChange={(value) => field.onChange(Boolean(value))}
                  />
                </div>
              )}
            />
          </div>
        );
      case AddonType.PerformanceAccountFee:
        return (
          <>
            <Controller
              control={control}
              name="addon_performance_account_fee_option"
              render={({ field }) => (
                <div className={'flex flex-col gap-3.5 flex-1 mt-6'}>
                  <p>Addon performance fee allowed</p>
                  <InputSelect
                    className={'text-xs'}
                    options={performanceAccountFeeData}
                    placeholder={'Please Select'}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                </div>
              )}
            />
          </>
        );
      case AddonType.Drawdown:
        return (
          <>
            <Controller
              control={control}
              name="drawdown_type"
              render={({ field }) => (
                <div className={'flex flex-col gap-y-1.5'}>
                  <RadioGroup
                    className={'flex gap-x-[18px]'}
                    value={field.value ?? ''}
                    onValueChange={(value) => field.onChange(value)}
                  >
                    {drawdownTypeData.map((item) => {
                      return (
                        <div
                          key={item.value}
                          className={'flex items-center gap-x-[18px]'}
                        >
                          <RadioGroupItem value={item.value} />
                          <p>{item.label}</p>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>
              )}
            />
            <Controller
              control={control}
              name="addon_additional_drawdown_option"
              render={({ field }) => (
                <div className={'flex flex-col gap-3.5 flex-1 mt-6'}>
                  <p>Addon drawdown allowed</p>
                  <InputSelect
                    className={'text-xs'}
                    options={drawdownData}
                    placeholder={'Please Select'}
                    value={field.value?.toString()}
                    onChange={(value) => field.onChange(value)}
                  />
                </div>
              )}
            />
          </>
        );
      case AddonType.AccountBundle:
        return (
          <>
            <Controller
              control={control}
              name="accounts_in_bundle"
              render={({ field }) => (
                <div className={'flex flex-col gap-2 flex-1'}>
                  <p>How many accounts in a bundle</p>
                  <Input
                    {...field}
                    placeholder={'Enter'}
                    textSize={'xs'}
                    type={'number'}
                    value={field.value ?? ''}
                    variant={'secondary'}
                  />
                </div>
              )}
            />
          </>
        );
      default:
        return <></>;
    }
  }, [addonType, control, productOptions]);

  const handleValueChange = useCallback(
    (
      field: ControllerRenderProps<AddonTypeData, 'addon_type'>,
      val: AddonType
    ) => {
      const currentValues = getValues();

      reset({
        ...currentValues,
        addon_type: val,
        addon_trading_days_required: undefined,
        addon_lifetime_performance_account_package_id: undefined,
        addon_extra_amount_allowed_payout: undefined,
        addon_extra_allowed_payout_time: undefined,
        addon_new_percent_override_default: undefined,
        addon_disable_percent_default: undefined,
        addon_discount_percent: undefined,
        addon_valid_for_days: undefined,
        drawdown_type: val === AddonType.Drawdown ? 'static' : undefined,
      });
      field.onChange(val);
    },
    [reset, getValues]
  );

  return (
    <div className={'flex-1'}>
      <Controller
        control={control}
        name="addon_type"
        render={({ field }) => (
          <div className={'flex flex-col gap-3.5 mt-6 mb-8.5'}>
            <p>Addon Type</p>
            <RadioGroup
              className={'flex flex-col gap-[18px]'}
              disabled={isEdit}
              value={field.value ?? ''}
              onValueChange={(value) =>
                handleValueChange(field, value as AddonType)
              }
            >
              {addonTypeData.map((item) => {
                return (
                  <div
                    key={item.value}
                    className={'flex items-center gap-x-[18px]'}
                  >
                    <RadioGroupItem value={item.value} />
                    <p>{item.label}</p>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        )}
      />
      {renderAddonContent()}
    </div>
  );
};
